import { ChangeEvent, FC, useEffect, useState } from 'react';
import Header from '../../../common/Header/Header';
import { H2WithMargin, MenuForm, RowWrapper, SaveButton } from '../EditMenu/EditMenu.styles';
import { InputWrapperLarge } from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Input from '../../../components/Input/Input';
import { useQueryClient } from 'react-query';
import {useBulkEditMenu} from '../../../services/menu/menu.service';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { showToast } from '../../../components/Toast/Toast';
import { AxiosError } from 'axios';
import { BulkEditMenuFormValues, BulkEditMenuProps } from './BulkEditMenu.types';
import SelectMenus from './SelectMenu/SelectMenu';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import { Organization } from '../../../services/menu/menu.types';

const BulkEditMenu: FC<BulkEditMenuProps> = (props) => {
  const navigateWithToast = useNavigateWithToast();

  const [selectedMenus, setSelectedMenus] = useState<Organization[]>([]);
  const { mutate, isError: editMenuIsError, isLoading, error: editMenuError } = useBulkEditMenu();

  const queryClient = useQueryClient();

  const [formValues, setFormValues] = useState<BulkEditMenuFormValues>({
    description: '',
    effectiveDate: ''
  });

  const checkedMenus = (selectedMenus: Organization[]) => {
    setSelectedMenus(selectedMenus)
  }
  const [initialFormValues] = useState<BulkEditMenuFormValues>({
    description: '',
    effectiveDate: ''
  });

  const checkForUnsavedChanges = () => {
    const didDescriptionOrEffectiveDateChange = initialFormValues.description !== formValues.description || initialFormValues.effectiveDate !== formValues.effectiveDate;
    setUnsavedChanges(didDescriptionOrEffectiveDateChange && selectedMenus.length > 0);
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues, selectedMenus]);

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  useGenericErrorMessage([{ isError: editMenuIsError, error: editMenuError }]);

  const onSuccess = () => {
    navigateWithToast(`/menus`, {
      label: 'Menus have been updated successfully.',
      id: 'menusUpdateSuccess',
    });
  }

  const onError = (error: any) => {
    queryClient.invalidateQueries([['menus']]);
    if ((error as AxiosError).message === 'apiGatewayTimeout') return;
    showToast(genericErrorShowToastOptions);
  }

  const handleSave = () => {
    let reqMenus = Array.from(selectedMenus, (menu) => parseInt(menu.id));

    // 2100 is the maximum number for parameters to be passed to SQL server (menuList + effective date + description + statusNo)
    if (reqMenus && reqMenus.length > props.menuLimit) {
      const chunkedMenuLists = reqMenus.reduce((resultArray: number[][], item, index) => {
        const chunkIndex = Math.floor(index/props.chunkSize);

        if(!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = []; // start a new chunk
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      }, []);
      chunkedMenuLists.forEach(reqMenus => {
        mutate({
          menus: reqMenus,
          description: formValues.description,
          effectiveDate: formValues.effectiveDate,
        }, {
          onSuccess: () => onSuccess(),
          onError: (error) => {
            onError(error);
          },
        })
      });
    } else {
      mutate(
        {
          menus: reqMenus,
          description: formValues.description,
          effectiveDate: formValues.effectiveDate,
        },
        {
          onSuccess: () => onSuccess(),
          onError: (error) => {
            onError(error);
          },
        }
      );
    }
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Menus', to: '/menus' }} title={`Menu Bulk Edit`} />
      <MenuForm>
        <H2WithMargin>Set Information</H2WithMargin>
        <RowWrapper>
          <InputWrapperLarge>
            <Input
              label='Effective Date'
              id='effectiveDate'
              type='date'
              value={formValues.effectiveDate}
              onChange={handleChange}
            />
          </InputWrapperLarge>
        </RowWrapper>
        <RowWrapper>
          <InputWrapperLarge>
            <Input
              label='Description'
              id='description'
              value={formValues.description}
              onChange={handleChange}
              maxLength={50}
            />
          </InputWrapperLarge>
        </RowWrapper>
        <SaveButton label='Save' testId='saveMenuBulkEdit' onClick={handleSave} disabled={!unsavedChanges} isLoading={isLoading} />
      </MenuForm>
      <SelectMenus
        checkedMenus={checkedMenus}
      />
    </>
  );
}

export default BulkEditMenu;